import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, AlertCircle, ArrowRight, Lock, Download } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /pay-balance/:orderId?token=...
 *
 * Customer lands here from the balance-ready email after Kaily marks the
 * shoot delivered. Token is an HMAC-signed string the backend verifies.
 *
 * Layout:
 *   - Greeting + package summary + balance amount
 *   - "Preview your files" link (if Kaily attached one)
 *   - Big gold "Pay balance" button → Stripe Checkout
 *   - If status is "paid_full" already: success state + download button
 */
export default function PayBalance() {
    const { lang } = useT();
    const isCn = lang === "cn";
    const { orderId } = useParams();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [state, setState] = useState("loading"); // loading | ready | paid | invalid | error
    const [order, setOrder] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/shoot-orders/balance/${orderId}`, {
                    params: { token },
                    timeout: 8000,
                });
                if (cancelled) return;
                setOrder(data);
                if (data.status === "paid_full") setState("paid");
                else setState("ready");
            } catch (err) {
                if (cancelled) return;
                if (err?.response?.status === 403) setState("invalid");
                else setState("error");
            }
        })();
        return () => { cancelled = true; };
    }, [orderId, token]);

    const startBalanceCheckout = async () => {
        setSubmitting(true);
        try {
            const { data } = await axios.post(
                `${API}/shoot-orders/balance/${orderId}/checkout`,
                {},
                {
                    params: { token, origin_url: window.location.origin },
                    timeout: 15000,
                },
            );
            if (data?.checkout_url) {
                window.location.href = data.checkout_url;
                return;
            }
            toast.error(isCn ? "无法创建支付会话" : "Could not start checkout");
        } catch (err) {
            toast.error(err?.response?.data?.detail || (isCn ? "支付服务暂时无法连接" : "Payment service unavailable"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-cafe-cream min-h-screen flex flex-col">
            <SiteNav />
            <main className="flex-1 flex items-center justify-center px-6 py-20 md:py-28">
                <div className="max-w-xl w-full">
                    {state === "loading" && (
                        <div className="text-center" data-testid="paybalance-loading">
                            <Loader2 className="h-10 w-10 mx-auto text-cafe-olive animate-spin mb-6" strokeWidth={1.5} />
                            <p className="text-cafe-mocha">{isCn ? "正在加载订单…" : "Loading your order…"}</p>
                        </div>
                    )}

                    {state === "invalid" && (
                        <div className="text-center" data-testid="paybalance-invalid">
                            <AlertCircle className="h-10 w-10 mx-auto text-cafe-terracotta mb-6" strokeWidth={1.5} />
                            <h1 className="font-display text-3xl text-cafe-espresso mb-3">
                                {isCn ? "链接无效或已过期" : "Invalid or expired link"}
                            </h1>
                            <p className="text-cafe-mocha text-sm leading-relaxed mb-6">
                                {isCn
                                    ? "请检查邮件中的链接是否完整复制。如果还有问题，请 WhatsApp Kaily。"
                                    : "Make sure you copied the full link from the email. If the issue persists, WhatsApp Kaily."}
                            </p>
                            <a
                                href="https://wa.me/60183628663"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-cafe-olive underline underline-offset-4 text-sm font-semibold"
                                data-testid="paybalance-wa-link"
                            >
                                WhatsApp Kaily →
                            </a>
                        </div>
                    )}

                    {state === "error" && (
                        <div className="text-center" data-testid="paybalance-error">
                            <AlertCircle className="h-10 w-10 mx-auto text-cafe-terracotta mb-6" strokeWidth={1.5} />
                            <h1 className="font-display text-3xl text-cafe-espresso mb-3">
                                {isCn ? "暂时无法加载" : "Something went wrong"}
                            </h1>
                            <p className="text-cafe-mocha text-sm">{isCn ? "请稍后重试。" : "Please try again in a moment."}</p>
                        </div>
                    )}

                    {state === "ready" && order && (
                        <div data-testid="paybalance-ready">
                            <div className="text-[11px] tracking-[0.28em] uppercase text-cafe-olive font-semibold mb-4">
                                {isCn ? "你的拍摄已完成" : "Your shoot is ready"}
                            </div>
                            <h1 className="font-display text-4xl md:text-5xl text-cafe-espresso mb-3 leading-[1.1]">
                                {isCn ? "支付尾款 · 解锁文件" : "Pay balance · unlock files"}
                            </h1>
                            <p className="text-cafe-mocha text-base md:text-lg leading-relaxed mb-8">
                                {isCn
                                    ? `${order.package_name_cn || order.package_name_en} · ${order.customer_name || ""} · ${order.customer_email}`
                                    : `${order.package_name_en} · ${order.customer_name || ""} · ${order.customer_email}`}
                            </p>

                            <div className="bg-white border border-cafe-espresso/10 rounded-2xl p-6 md:p-8 mb-8">
                                <div className="flex items-baseline justify-between mb-5 pb-5 border-b border-cafe-espresso/10">
                                    <span className="text-cafe-mocha text-sm">{isCn ? "套餐总价" : "Package total"}</span>
                                    <span className="font-display text-2xl text-cafe-espresso">RM {(order.total_cents / 100).toFixed(2)}</span>
                                </div>
                                <div className="flex items-baseline justify-between mb-3">
                                    <span className="text-cafe-olive text-sm flex items-center gap-1.5">
                                        <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> {isCn ? "定金已付" : "Deposit paid"}
                                    </span>
                                    <span className="text-cafe-olive text-sm">RM {(order.deposit_cents / 100).toFixed(2)}</span>
                                </div>
                                <div className="flex items-baseline justify-between">
                                    <span className="font-display text-lg text-cafe-espresso">{isCn ? "尾款待付" : "Balance due"}</span>
                                    <span className="font-display text-3xl md:text-4xl text-[#B88A55] tracking-[-0.02em]">
                                        RM {(order.balance_cents / 100).toFixed(2)}
                                    </span>
                                </div>
                            </div>

                            {order.delivery_preview_url && (
                                <a
                                    href={order.delivery_preview_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="paybalance-preview"
                                    className="block mb-5 p-5 bg-cafe-espresso/5 hover:bg-cafe-espresso/10 border border-cafe-espresso/10 rounded-2xl text-cafe-espresso transition-colors"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-medium text-sm">{isCn ? "→ 预览你的拍摄成果（水印）" : "→ Preview your shoot (watermarked)"}</span>
                                        <ArrowRight className="h-4 w-4" strokeWidth={2} />
                                    </div>
                                </a>
                            )}

                            {order.delivery_notes && (
                                <div className="mb-6 p-4 bg-cafe-latte/40 border border-cafe-espresso/10 rounded-xl text-sm text-cafe-mocha leading-relaxed italic">
                                    {order.delivery_notes}
                                </div>
                            )}

                            <button
                                onClick={startBalanceCheckout}
                                disabled={submitting}
                                data-testid="paybalance-cta"
                                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-medium tracking-wide bg-gradient-to-br from-[#E8B97A] to-[#B88A55] text-[#1A0F0A] hover:from-[#F0C384] hover:to-[#C39562] shadow-[0_15px_35px_-12px_rgba(212,165,116,0.65)] disabled:opacity-60 disabled:cursor-wait transition-all"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                                        {isCn ? "正在跳转 Stripe…" : "Redirecting to Stripe…"}
                                    </>
                                ) : (
                                    <>
                                        <Lock className="h-4 w-4" strokeWidth={2} />
                                        {isCn ? `安全支付 RM ${(order.balance_cents / 100).toFixed(2)}` : `Pay RM ${(order.balance_cents / 100).toFixed(2)} securely`}
                                    </>
                                )}
                            </button>

                            <p className="text-[10px] text-cafe-mocha/60 text-center mt-4 leading-relaxed">
                                {isCn
                                    ? "Stripe 安全支付 · 支持 Visa / Mastercard / FPX · 付款成功立即解锁下载链接"
                                    : "Secured by Stripe · Visa / Mastercard / FPX · download link unlocks instantly on payment"}
                            </p>
                        </div>
                    )}

                    {state === "paid" && order && (
                        <div className="text-center" data-testid="paybalance-paid">
                            <div className="h-16 w-16 mx-auto rounded-full bg-cafe-olive/15 flex items-center justify-center mb-6">
                                <Check className="h-8 w-8 text-cafe-olive" strokeWidth={2.2} />
                            </div>
                            <h1 className="font-display text-4xl text-cafe-espresso mb-3 leading-[1.1]">
                                {isCn ? "已付清 · 文件已解锁" : "Paid in full · files unlocked"}
                            </h1>
                            <p className="text-cafe-mocha text-base mb-8 leading-relaxed">
                                {isCn ? "谢谢你！享受你的新作品。" : "Thank you. Enjoy your new images."}
                            </p>
                            {order.delivery_files_url && (
                                <a
                                    href={order.delivery_files_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="paybalance-download"
                                    className="inline-flex items-center gap-2 px-6 py-4 rounded-full font-medium tracking-wide bg-cafe-espresso hover:bg-[#1A0F0A] text-cafe-cream transition-all"
                                >
                                    <Download className="h-4 w-4" strokeWidth={2} />
                                    {isCn ? "下载全部文件" : "Download your files"}
                                </a>
                            )}
                            <div className="mt-8">
                                <Link to="/" className="text-cafe-olive text-sm underline underline-offset-4 font-semibold">
                                    {isCn ? "回首页" : "Back home"}
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </main>
            <SiteFooter />
        </div>
    );
}


/**
 * /pay-balance/success?session_id=...&order_id=... — final thank-you after balance paid.
 * Polls status briefly then redirects to /pay-balance/:orderId which will show
 * the paid state with the download link.
 */
export function PayBalanceSuccess() {
    const { lang } = useT();
    const isCn = lang === "cn";
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const orderId = params.get("order_id");
    const [state, setState] = useState("loading"); // loading | done

    useEffect(() => {
        let cancelled = false;
        let attempts = 0;
        const poll = async () => {
            if (cancelled) return;
            attempts += 1;
            try {
                await axios.get(`${API}/shoot-checkout/status/${sessionId}`, { timeout: 8000 });
            } catch { /* tolerate */ }
            if (attempts >= 3) {
                if (!cancelled) setState("done");
                return;
            }
            setTimeout(poll, 2000);
        };
        if (sessionId) poll();
        else setState("done");
        return () => { cancelled = true; };
    }, [sessionId]);

    return (
        <div className="bg-cafe-cream min-h-screen flex flex-col">
            <SiteNav />
            <main className="flex-1 flex items-center justify-center px-6 py-20">
                <div className="max-w-md w-full text-center">
                    {state === "loading" ? (
                        <>
                            <Loader2 className="h-10 w-10 mx-auto text-cafe-olive animate-spin mb-6" strokeWidth={1.5} />
                            <p className="text-cafe-mocha">{isCn ? "正在确认付款…" : "Confirming payment…"}</p>
                        </>
                    ) : (
                        <div data-testid="paybalance-success-done">
                            <div className="h-16 w-16 mx-auto rounded-full bg-cafe-olive/15 flex items-center justify-center mb-6">
                                <Check className="h-8 w-8 text-cafe-olive" strokeWidth={2.2} />
                            </div>
                            <h1 className="font-display text-4xl text-cafe-espresso mb-4">
                                {isCn ? "付款成功 ☕" : "Payment successful ☕"}
                            </h1>
                            <p className="text-cafe-mocha mb-8 leading-relaxed">
                                {isCn
                                    ? "我们已发送一封邮件 · 含完整下载链接。请查收邮箱。"
                                    : "We've sent an email with the full download link. Check your inbox."}
                            </p>
                            {orderId && (
                                <Link
                                    to={`/pay-balance/${orderId}`}
                                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-cafe-espresso text-cafe-cream text-sm hover:bg-[#1A0F0A] transition-colors"
                                    data-testid="paybalance-success-back"
                                >
                                    {isCn ? "查看我的订单" : "View my order"} <ArrowRight className="h-4 w-4" />
                                </Link>
                            )}
                        </div>
                    )}
                </div>
            </main>
            <SiteFooter />
        </div>
    );
}
